'use client';

import React from 'react';

import Link from 'next/link';

import { Info, CreditCard, ArrowRight } from 'lucide-react';

import CreditCardIntro from '@/components/cards/CreditCardIntro';
import { Card, CardContent } from '@/components/ui/card';
import MoneyResources from '@/components/ui/MoneyResources';
import { CardChannel, cardChannels } from '@/data/credit-cards/models/channels';
import { channelCategories } from '@/data/credit-cards/ui/channelCategories';
import { Card as CardType } from '@/types/Card';

interface ChannelCardsLayoutProps {
  channel: CardChannel;
}

// Helper function to get related channels based on the current channel
const getRelatedChannels = (currentChannel: CardChannel): CardChannel[] => {
  // Find which category this channel belongs to
  const category = channelCategories.find(cat => cat.channelIds.includes(currentChannel.id));

  if (!category) return [];

  // Get other channels from the same category, excluding the current one
  const relatedChannelIds = category.channelIds.filter(id => id !== currentChannel.id);

  // Get the channel objects for the related channel IDs
  const relatedChannels = cardChannels.filter(channel => relatedChannelIds.includes(channel.id));

  // Return up to 3 related channels
  return relatedChannels.slice(0, 3);
};

export default function ChannelCardsLayout({ channel }: ChannelCardsLayoutProps) {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Cards Section */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {channel.name}推薦卡片
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            以下是在{channel.name}通路有高回饋的信用卡推薦
          </p>
        </div>

        {/* Cards Grid */}
        <div className="space-y-6">
          {channel.cards.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {channel.cards.map((card: CardType) => (
                <div
                  key={card.key as string}
                  className="transition-all duration-300 hover:transform hover:scale-[1.02]"
                >
                  <CreditCardIntro card={card} numFeature={3} />
                </div>
              ))}
            </div>
          ) : (
            <Card className="text-center py-10 px-6 shadow-md">
              <CardContent>
                <div className="mb-6 flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 dark:text-blue-400">
                    <Info className="h-8 w-8" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  目前此通路尚無推薦信用卡
                </h3>
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  我們正在持續更新各大通路的信用卡推薦，請稍後再回來查看
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Related Channels Section */}
      <div className="bg-gray-50 dark:bg-gray-800 py-8">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">相關通路</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {getRelatedChannels(channel).map(relatedChannel => (
              <Link
                key={relatedChannel.id}
                href={`/credit-cards/channel/${relatedChannel.id}`}
                className="group flex items-center p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-500 transition-all duration-200 hover:shadow-md"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 dark:text-blue-400 mr-3">
                  <CreditCard className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {relatedChannel.name}
                  </h3>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </Link>
            ))}

            {/* View All Channels Link */}
            <Link
              href="/credit-cards/channel"
              className="group flex items-center p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-500 transition-all duration-200 hover:shadow-md"
            >
              <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 dark:text-indigo-400 mr-3">
                <ArrowRight className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  查看所有通路
                </h3>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
